// ===================================================================
// Time & Labor — Personal Time Portal
// Simplified view for basic time entry users (timelabor_only role).
// Shows only the user's own time entries and profile.
// ===================================================================

import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, Table, Button, Group, TextInput, Select, Modal,
  SimpleGrid, Badge, ActionIcon, Stack, Divider, NumberInput, Tabs,
  Checkbox,
} from '../../components/gab/GabComponents';
import {
  IconClock, IconPlus, IconTrash, IconCalendarPlus, IconEdit,
  IconDeviceFloppy, IconSend, IconUser, IconAlertTriangle,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useTimeLaborStore } from '../../store/timeLaborStore';
import { useAuthStore } from '../../store/authStore';
import { formatCurrency, stripCurrency } from '../../lib/formatUtils';
import { WORKER_URL } from '../../lib/api-switch';

const TEAL = '#0D9488';

const STATUS_COLORS: Record<string, string> = {
  draft: 'gray', submitted: 'blue', approved: 'green', rejected: 'red',
};

const TASK_TYPES = [
  { value: 'debris_removal', label: 'Debris Removal', category: 'A' },
  { value: 'traffic_control', label: 'Traffic Control', category: 'B' },
  { value: 'search_rescue', label: 'Search & Rescue', category: 'B' },
  { value: 'eoc_operations', label: 'EOC Operations', category: 'B' },
  { value: 'shelter_management', label: 'Shelter Management', category: 'B' },
  { value: 'emergency_protective', label: 'Emergency Protective Measures', category: 'B' },
  { value: 'security_patrol', label: 'Security / Law Enforcement', category: 'B' },
  { value: 'road_bridge_repair', label: 'Road & Bridge Repair', category: 'C' },
  { value: 'water_sewer_repair', label: 'Water / Sewer Repair', category: 'D' },
  { value: 'building_repair', label: 'Building Repair', category: 'E' },
  { value: 'utility_restoration', label: 'Utility Restoration', category: 'F' },
  { value: 'park_recreation', label: 'Park & Recreation Repair', category: 'G' },
  { value: 'admin_management', label: 'Administrative / Management', category: 'Z' },
  { value: 'other', label: 'Other', category: '' },
];
const TASK_OPTIONS = TASK_TYPES.map(t => ({ value: t.value, label: t.label }));
function getTaskLabel(v: string) { return TASK_TYPES.find(t => t.value === v)?.label || v; }
function getTaskCategory(v: string) { return TASK_TYPES.find(t => t.value === v)?.category || ''; }

function pf(v: string | number | undefined) { if (typeof v === 'number') return v || 0; return parseFloat(stripCurrency(v || '')) || 0; }
function todayStr() { return new Date().toISOString().slice(0, 10); }

function getMondayOfWeek(dateStr: string) {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return d;
}
function fmtDate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

interface TimecardRow { id: number; date: string; regHours: number; otHours: number; notes: string; site: string; task: string; shiftPremium: string; accountCode: string; programCode: string; }
interface CodeEntry { code: string; description: string; }
interface ShiftPremiumEntry { type: string; label: string; multiplier: number; }

const DEFAULT_PREMIUMS: ShiftPremiumEntry[] = [
  { type: 'night', label: 'Night Differential', multiplier: 1.1 },
  { type: 'weekend', label: 'Weekend', multiplier: 1.5 },
  { type: 'holiday', label: 'Holiday', multiplier: 2.0 },
  { type: 'hazard', label: 'Hazard Pay', multiplier: 1.25 },
];

const PREMIUM_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'night', label: 'Night Differential' },
  { value: 'weekend', label: 'Weekend' },
  { value: 'holiday', label: 'Holiday' },
  { value: 'hazard', label: 'Hazard Pay' },
];

let _rowId = 1;
function newRow(date?: string): TimecardRow { return { id: _rowId++, date: date || todayStr(), regHours: 0, otHours: 0, notes: '', site: '', task: '', shiftPremium: 'none', accountCode: '', programCode: '' }; }

export default function MyTimePortal() {
  const user = useAuthStore(s => s.user);
  const activeIncident = useAuthStore(s => s.activeIncident);
  const {
    employees, fetchEmployees,
    laborRecords, fetchLaborRecords,
    createLaborRecord, updateLaborRecord, deleteLaborRecord,
  } = useTimeLaborStore();

  const [activeTab, setActiveTab] = useState<string | null>('entry');

  // Codes & premiums
  const [accountCodes, setAccountCodes] = useState<CodeEntry[]>([]);
  const [programCodes, setProgramCodes] = useState<CodeEntry[]>([]);
  const [shiftPremiums, setShiftPremiums] = useState<ShiftPremiumEntry[]>(DEFAULT_PREMIUMS);
  const [sameCodes, setSameCodes] = useState(true);
  const [globalAccountCode, setGlobalAccountCode] = useState('');
  const [globalProgramCode, setGlobalProgramCode] = useState('');

  useEffect(() => { fetchEmployees(); fetchLaborRecords(); loadCodeConfig(); }, []);

  const loadCodeConfig = async () => {
    const kvH = { Origin: 'https://apps.opengov.com' };
    try {
      const [acRes, pcRes, spRes] = await Promise.all([
        fetch(`${WORKER_URL}/api/config/timelabor-account-codes`, { headers: kvH }),
        fetch(`${WORKER_URL}/api/config/timelabor-program-codes`, { headers: kvH }),
        fetch(`${WORKER_URL}/api/config/timelabor-shift-premiums`, { headers: kvH }),
      ]);
      if (acRes.ok) { const d = await acRes.json(); if (Array.isArray(d)) setAccountCodes(d); }
      if (pcRes.ok) { const d = await pcRes.json(); if (Array.isArray(d)) setProgramCodes(d); }
      if (spRes.ok) { const d = await spRes.json(); if (Array.isArray(d) && d.length > 0) setShiftPremiums(d.filter((p: ShiftPremiumEntry) => p.type !== 'none')); }
    } catch { /* ignore */ }
  };

  const myEmail = user?.email?.toLowerCase() || '';
  const myName = user?.fullName || user?.email || '';

  // Find my labor profile
  const myProfile = useMemo(() =>
    employees.find(e => e.rosterEmail?.toLowerCase() === myEmail),
    [employees, myEmail]
  );

  // Auto-fill default codes from profile
  useEffect(() => {
    if (myProfile) {
      if (myProfile.defaultAccountCode) setGlobalAccountCode(myProfile.defaultAccountCode);
      if (myProfile.defaultProgramCode) setGlobalProgramCode(myProfile.defaultProgramCode);
    }
  }, [myProfile]);

  const getPremiumMultiplier = useCallback((premType: string): number => {
    if (!premType || premType === 'none') return 1.0;
    const found = shiftPremiums.find(p => p.type === premType);
    return found ? found.multiplier : 1.0;
  }, [shiftPremiums]);

  // My records only
  const myRecords = useMemo(() => {
    let list = laborRecords.filter(r =>
      r.employeeName?.toLowerCase() === myName.toLowerCase() ||
      laborRecords.some(lr => lr.employeeName === r.employeeName && employees.find(e => e.rosterEmail?.toLowerCase() === myEmail && e.employeeName === r.employeeName))
    );
    if (activeIncident) {
      const incId = String(activeIncident.id);
      list = list.filter(r => !r.incidentId || String(r.incidentId) === incId);
    }
    return [...list].sort((a, b) => b.workDate.localeCompare(a.workDate));
  }, [laborRecords, myName, myEmail, employees, activeIncident]);

  // Stats
  const totalHours = myRecords.reduce((s, r) => s + pf(r.hoursRegular) + pf(r.hoursOvertime), 0);
  const draftCount = myRecords.filter(r => !r.timesheetOnFile || r.timesheetOnFile === 'draft' || r.timesheetOnFile === 'no').length;
  const submittedCount = myRecords.filter(r => r.timesheetOnFile === 'submitted').length;
  const approvedCount = myRecords.filter(r => r.timesheetOnFile === 'approved').length;
  const rejectedCount = myRecords.filter(r => r.timesheetOnFile === 'rejected').length;

  // ── Time Entry Form ────────────────────────────────────────────
  const [sameJobSite, setSameJobSite] = useState(true);
  const [globalSite, setGlobalSite] = useState('');
  const [sameActivity, setSameActivity] = useState(true);
  const [globalTask, setGlobalTask] = useState<string | null>(null);
  const [rows, setRows] = useState<TimecardRow[]>([newRow()]);
  const [submitting, setSubmitting] = useState(false);

  const updateRow = useCallback((id: number, patch: Partial<TimecardRow>) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  }, []);

  const addWeek = useCallback(() => {
    const monday = getMondayOfWeek(todayStr());
    const newRows: TimecardRow[] = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(monday);
      d.setDate(d.getDate() + i);
      newRows.push(newRow(fmtDate(d)));
    }
    setRows(prev => [...prev, ...newRows]);
  }, []);

  async function handleSubmit() {
    if (!myProfile) {
      notifications.show({ title: 'No Profile', message: 'You need a labor profile set up. Contact your supervisor.', color: 'red' });
      return;
    }
    const validRows = rows.filter(r => r.date && (r.regHours > 0 || r.otHours > 0));
    if (validRows.length === 0) {
      notifications.show({ title: 'No Hours', message: 'Enter hours for at least one date.', color: 'orange' });
      return;
    }
    setSubmitting(true);
    const regRate = pf(myProfile.regularRate);
    const otRate = pf(myProfile.overtimeRate);
    const fringePct = pf(myProfile.totalFringePct);
    let created = 0;

    for (const row of validRows) {
      const taskVal = sameActivity ? globalTask : row.task;
      const taskLabel = taskVal ? getTaskLabel(taskVal) : 'General';
      const taskCategory = taskVal ? getTaskCategory(taskVal) : '';
      const site = sameJobSite ? globalSite : row.site;
      const premMult = getPremiumMultiplier(row.shiftPremium);
      const laborCost = (row.regHours * regRate * premMult) + (row.otHours * otRate * premMult);
      const fringeCost = laborCost * fringePct / 100;

      await createLaborRecord({
        projectId: '',
        organizationId: myProfile.organizationId || '',
        employeeName: myProfile.employeeName || myName,
        employeeTitle: myProfile.jobTitle || '',
        employeeId: myProfile.employeeId || '',
        workDate: row.date,
        hoursRegular: row.regHours.toString(),
        hoursOvertime: row.otHours.toString(),
        rateRegular: myProfile.regularRate,
        rateOvertime: myProfile.overtimeRate,
        fringeBenefitsPct: myProfile.totalFringePct,
        laborCost: laborCost.toFixed(2),
        fringeCost: fringeCost.toFixed(2),
        totalCost: (laborCost + fringeCost).toFixed(2),
        activityDescription: row.notes ? `${taskLabel}: ${row.notes}` : taskLabel,
        category: taskCategory,
        timesheetOnFile: 'draft',
        notes: site || '',
        incidentId: activeIncident?.id ? String(activeIncident.id) : '',
        accountCode: sameCodes ? globalAccountCode : (row.accountCode || globalAccountCode),
        programCode: sameCodes ? globalProgramCode : (row.programCode || globalProgramCode),
        shiftPremium: row.shiftPremium || 'none',
      });
      created++;
    }

    notifications.show({ title: 'Saved', message: `${created} time entries created`, color: TEAL });
    setRows([newRow()]);
    setSubmitting(false);
  }

  // ── Bulk submit ────────────────────────────────────────────────
  const [bulkSelected, setBulkSelected] = useState<Set<number>>(new Set());

  async function handleBulkSubmit() {
    for (const rid of bulkSelected) {
      await updateLaborRecord(rid, { timesheetOnFile: 'submitted' });
    }
    notifications.show({ title: 'Submitted', message: `${bulkSelected.size} entries submitted for review`, color: 'blue' });
    setBulkSelected(new Set());
  }

  // ── Edit/Delete ────────────────────────────────────────────
  const [detailEntry, setDetailEntry] = useState<any>(null);
  const [editingEntry, setEditingEntry] = useState<any>(null);
  const [editForm, setEditForm] = useState({ regHours: '', otHours: '', task: '', site: '', notes: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  function openEdit(r: any) {
    const taskMatch = TASK_TYPES.find(t => r.activityDescription?.startsWith(t.label));
    setEditForm({
      regHours: String(pf(r.hoursRegular)),
      otHours: String(pf(r.hoursOvertime)),
      task: taskMatch?.value || '',
      site: r.notes || '',
      notes: r.activityDescription?.includes(': ') ? r.activityDescription.split(': ').slice(1).join(': ') : '',
    });
    setEditingEntry(r);
  }

  async function handleEditSave() {
    if (!editingEntry || !myProfile) return;
    setEditSaving(true);
    const regHrs = parseFloat(editForm.regHours) || 0;
    const otHrs = parseFloat(editForm.otHours) || 0;
    const regRate = pf(myProfile.regularRate);
    const otRate = pf(myProfile.overtimeRate);
    const fringePct = pf(myProfile.totalFringePct);
    const laborCost = (regHrs * regRate) + (otHrs * otRate);
    const fringeCost = laborCost * fringePct / 100;
    const taskLabel = editForm.task ? getTaskLabel(editForm.task) : 'General';
    const taskCategory = editForm.task ? getTaskCategory(editForm.task) : '';
    const actDesc = editForm.notes ? `${taskLabel}: ${editForm.notes}` : taskLabel;

    await updateLaborRecord(editingEntry.Rid, {
      hoursRegular: String(regHrs),
      hoursOvertime: String(otHrs),
      laborCost: laborCost.toFixed(2),
      fringeCost: fringeCost.toFixed(2),
      totalCost: (laborCost + fringeCost).toFixed(2),
      activityDescription: actDesc,
      category: taskCategory,
      notes: editForm.site,
    });
    notifications.show({ title: 'Updated', message: 'Time entry updated', color: TEAL });
    setEditSaving(false);
    setEditingEntry(null);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    await deleteLaborRecord(deleteTarget.Rid);
    notifications.show({ title: 'Deleted', message: 'Time entry removed', color: 'red' });
    setDeleteTarget(null);
  }

  // ── No profile state ──────────────────────────────────────────
  if (!myProfile) {
    return (
      <Stack gap="md">
        <Title order={3}>My Timesheet</Title>
        <Paper p="xl" withBorder ta="center">
          <IconUser size={48} color="gray" />
          <Title order={4} mt="md" c="dimmed">No Labor Profile Found</Title>
          <Text c="dimmed" mt="xs">Your account ({myEmail}) doesn't have a labor profile set up yet.</Text>
          <Text c="dimmed">Contact your supervisor or grant administrator to create your profile with pay rates and classification.</Text>
        </Paper>
      </Stack>
    );
  }

  const draftRecords = myRecords.filter(r => !r.timesheetOnFile || r.timesheetOnFile === 'draft' || r.timesheetOnFile === 'no');

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Title order={3}>My Timesheet</Title>
          <Text size="sm" c="dimmed">{myProfile.employeeName} — {myProfile.jobTitle}</Text>
        </div>
        <Group gap="xs">
          <Badge variant="light" color={TEAL}>{formatCurrency(myProfile.regularRate)}/hr</Badge>
          <Badge variant="light" color="gray">{myProfile.totalFringePct}% fringe</Badge>
        </Group>
      </Group>

      {/* Stats */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
        <Paper p="sm" withBorder ta="center" style={{ borderTop: `3px solid ${TEAL}` }}>
          <Text size="xl" fw={700}>{totalHours.toFixed(1)}</Text>
          <Text size="xs" c="dimmed">Total Hours</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid gray' }}>
          <Text size="xl" fw={700}>{draftCount}</Text>
          <Text size="xs" c="dimmed">Draft</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #3B82F6' }}>
          <Text size="xl" fw={700}>{submittedCount}</Text>
          <Text size="xs" c="dimmed">Submitted</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #10B981' }}>
          <Text size="xl" fw={700}>{approvedCount}</Text>
          <Text size="xs" c="dimmed">Approved</Text>
        </Paper>
      </SimpleGrid>

      {/* Rejected alert */}
      {rejectedCount > 0 && (
        <Paper p="sm" withBorder style={{ borderLeft: '3px solid #EF4444' }}>
          <Group gap="xs">
            <IconAlertTriangle size={16} color="#EF4444" />
            <Text size="sm" fw={500} c="red">{rejectedCount} rejected — review notes and resubmit</Text>
          </Group>
        </Paper>
      )}

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="entry" leftSection={<IconPlus size={14} />}>Enter Time</Tabs.Tab>
          <Tabs.Tab value="history" leftSection={<IconClock size={14} />}>My Entries ({myRecords.length})</Tabs.Tab>
        </Tabs.List>

        {/* ── ENTER TIME ──────────────────────────────────── */}
        <Tabs.Panel value="entry" pt="md">
          <Paper p="md" withBorder>
            <Stack gap="sm">
              <Group gap="md">
                <Checkbox label="Same job site for all" checked={sameJobSite} onChange={e => setSameJobSite(e.currentTarget.checked)} />
                <Checkbox label="Same activity for all" checked={sameActivity} onChange={e => setSameActivity(e.currentTarget.checked)} />
                {(accountCodes.length > 0 || programCodes.length > 0) && (
                  <Checkbox label="Same codes for all" checked={sameCodes} onChange={e => setSameCodes(e.currentTarget.checked)} />
                )}
              </Group>
              {sameJobSite && (
                <TextInput label="Job Site / Location" value={globalSite} onChange={e => setGlobalSite(e.target.value)} size="sm" />
              )}
              {sameActivity && (
                <Select label="Activity" data={TASK_OPTIONS} value={globalTask} onChange={setGlobalTask} placeholder="Select activity..." clearable size="sm" />
              )}
              {sameCodes && (accountCodes.length > 0 || programCodes.length > 0) && (
                <Group gap="sm">
                  {accountCodes.length > 0 && (
                    <Select label="Account Code" size="sm" clearable searchable style={{ flex: 1 }}
                      data={accountCodes.map(c => ({ value: c.code, label: `${c.code} — ${c.description}` }))}
                      value={globalAccountCode || null}
                      onChange={v => setGlobalAccountCode(v || '')} placeholder="Select..." />
                  )}
                  {programCodes.length > 0 && (
                    <Select label="Program Code" size="sm" clearable searchable style={{ flex: 1 }}
                      data={programCodes.map(c => ({ value: c.code, label: `${c.code} — ${c.description}` }))}
                      value={globalProgramCode || null}
                      onChange={v => setGlobalProgramCode(v || '')} placeholder="Select..." />
                  )}
                </Group>
              )}

              <Divider label="Time Entries" labelPosition="left" />

              {rows.map((row, idx) => (
                <Group key={row.id} gap="xs" align="flex-end">
                  <TextInput label={idx === 0 ? 'Date' : undefined} type="date" value={row.date}
                    onChange={e => updateRow(row.id, { date: e.target.value })} size="sm" style={{ width: 150 }} />
                  <NumberInput label={idx === 0 ? 'Reg Hrs' : undefined} min={0} max={24} decimalScale={1}
                    value={row.regHours || ''} onChange={v => updateRow(row.id, { regHours: typeof v === 'number' ? v : 0 })} size="sm" style={{ width: 80 }} />
                  <NumberInput label={idx === 0 ? 'OT Hrs' : undefined} min={0} max={24} decimalScale={1}
                    value={row.otHours || ''} onChange={v => updateRow(row.id, { otHours: typeof v === 'number' ? v : 0 })} size="sm" style={{ width: 80 }} />
                  {!sameActivity && (
                    <Select label={idx === 0 ? 'Activity' : undefined} data={TASK_OPTIONS} value={row.task || null}
                      onChange={v => updateRow(row.id, { task: v || '' })} size="sm" style={{ width: 180 }} placeholder="Activity..." />
                  )}
                  {!sameJobSite && (
                    <TextInput label={idx === 0 ? 'Site' : undefined} value={row.site}
                      onChange={e => updateRow(row.id, { site: e.target.value })} size="sm" style={{ width: 140 }} />
                  )}
                  {!sameCodes && accountCodes.length > 0 && (
                    <Select label={idx === 0 ? 'Account' : undefined} placeholder="Account"
                      data={accountCodes.map(c => ({ value: c.code, label: c.code }))}
                      value={row.accountCode || null}
                      onChange={v => updateRow(row.id, { accountCode: v || '' })} size="sm" style={{ width: 120 }} clearable />
                  )}
                  {!sameCodes && programCodes.length > 0 && (
                    <Select label={idx === 0 ? 'Program' : undefined} placeholder="Program"
                      data={programCodes.map(c => ({ value: c.code, label: c.code }))}
                      value={row.programCode || null}
                      onChange={v => updateRow(row.id, { programCode: v || '' })} size="sm" style={{ width: 120 }} clearable />
                  )}
                  <Select label={idx === 0 ? 'Premium' : undefined} data={PREMIUM_OPTIONS}
                    value={row.shiftPremium || 'none'}
                    onChange={v => updateRow(row.id, { shiftPremium: v || 'none' })} size="sm" style={{ width: 140 }} />
                  <TextInput label={idx === 0 ? 'Notes' : undefined} value={row.notes}
                    onChange={e => updateRow(row.id, { notes: e.target.value })} size="sm" style={{ flex: 1 }} />
                  {rows.length > 1 && (
                    <ActionIcon size="sm" color="red" variant="subtle" onClick={() => setRows(prev => prev.filter(r => r.id !== row.id))}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  )}
                </Group>
              ))}

              <Group gap="xs">
                <Button variant="light" size="xs" color={TEAL} leftSection={<IconPlus size={14} />}
                  onClick={() => setRows(prev => [...prev, newRow()])}>Add Date</Button>
                <Button variant="light" size="xs" color="blue" leftSection={<IconCalendarPlus size={14} />}
                  onClick={addWeek}>Add Week (Mon-Fri)</Button>
              </Group>

              <Divider />
              <Group justify="flex-end">
                <Button color={TEAL} leftSection={<IconDeviceFloppy size={16} />}
                  onClick={handleSubmit} loading={submitting}>Save Timecard</Button>
              </Group>
            </Stack>
          </Paper>
        </Tabs.Panel>

        {/* ── MY ENTRIES ──────────────────────────────────── */}
        <Tabs.Panel value="history" pt="md">
          {/* Bulk submit bar */}
          {bulkSelected.size > 0 && (
            <Paper p="xs" withBorder mb="md" bg="blue.0">
              <Group justify="space-between">
                <Text size="sm" fw={500}>{bulkSelected.size} draft entries selected</Text>
                <Button size="xs" color="blue" leftSection={<IconSend size={14} />} onClick={handleBulkSubmit}>
                  Submit for Review
                </Button>
              </Group>
            </Paper>
          )}

          <Paper withBorder>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={40}>
                    <Checkbox size="xs"
                      checked={draftRecords.length > 0 && draftRecords.every(r => bulkSelected.has(r.Rid))}
                      indeterminate={bulkSelected.size > 0 && !draftRecords.every(r => bulkSelected.has(r.Rid))}
                      onChange={() => {
                        if (draftRecords.every(r => bulkSelected.has(r.Rid))) setBulkSelected(new Set());
                        else setBulkSelected(new Set(draftRecords.map(r => r.Rid)));
                      }}
                    />
                  </Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Activity</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Reg</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>OT</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Total $</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Premium</Table.Th>
                  <Table.Th>Site</Table.Th>
                  <Table.Th w={70}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {myRecords.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={10}><Text size="sm" c="dimmed" ta="center" py="lg">No time entries yet.</Text></Table.Td>
                  </Table.Tr>
                ) : myRecords.map(r => {
                  const isDraft = !r.timesheetOnFile || r.timesheetOnFile === 'draft' || r.timesheetOnFile === 'no';
                  const isRejected = r.timesheetOnFile === 'rejected';
                  const rejectionNote = isRejected && r.reviewerNotes ? r.reviewerNotes : (isRejected && r.notes?.startsWith('[REJECTED:') ? r.notes.match(/\[REJECTED:\s*(.*?)\]/)?.[1] : '');
                  return (
                    <Table.Tr key={r.Rid} style={{ background: isRejected ? 'var(--mantine-color-red-0)' : undefined, cursor: 'pointer' }} onClick={() => setDetailEntry(r)}>
                      <Table.Td onClick={e => e.stopPropagation()}>
                        {isDraft && (
                          <Checkbox size="xs" checked={bulkSelected.has(r.Rid)}
                            onChange={() => setBulkSelected(prev => {
                              const next = new Set(prev);
                              if (next.has(r.Rid)) next.delete(r.Rid); else next.add(r.Rid);
                              return next;
                            })} />
                        )}
                      </Table.Td>
                      <Table.Td>{r.workDate}</Table.Td>
                      <Table.Td>
                        <Text size="sm" truncate style={{ maxWidth: 200 }}>{r.activityDescription}</Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{pf(r.hoursRegular).toFixed(1)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{pf(r.hoursOvertime) > 0 ? pf(r.hoursOvertime).toFixed(1) : '-'}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }} fw={600}>{formatCurrency(r.totalCost)}</Table.Td>
                      <Table.Td>
                        <Badge size="sm" variant="light" color={STATUS_COLORS[r.timesheetOnFile] || 'gray'}>
                          {r.timesheetOnFile || 'draft'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {r.shiftPremium && r.shiftPremium !== 'none' && (
                          <Badge size="xs" variant="light" color="violet">
                            {PREMIUM_OPTIONS.find(p => p.value === r.shiftPremium)?.label || r.shiftPremium}
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed" truncate style={{ maxWidth: 120 }}>{r.notes || '-'}</Text>
                        {rejectionNote && (
                          <Text size="xs" c="red" mt={2}>Reviewer: {rejectionNote}</Text>
                        )}
                      </Table.Td>
                      <Table.Td onClick={e => e.stopPropagation()}>
                        {(isDraft || isRejected) && (
                          <Group gap={4} wrap="nowrap">
                            <ActionIcon size="xs" variant="subtle" color="blue" onClick={() => openEdit(r)}>
                              <IconEdit size={14} />
                            </ActionIcon>
                            <ActionIcon size="xs" variant="subtle" color="red" onClick={() => setDeleteTarget(r)}>
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Group>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Paper>
        </Tabs.Panel>
      </Tabs>

      {/* Detail Modal */}
      <Modal opened={!!detailEntry} onClose={() => setDetailEntry(null)} title="Time Entry Detail" size="md">
        {detailEntry && (() => {
          const isDraft = !detailEntry.timesheetOnFile || detailEntry.timesheetOnFile === 'draft' || detailEntry.timesheetOnFile === 'no';
          const isRejected = detailEntry.timesheetOnFile === 'rejected';
          const regHrs = pf(detailEntry.hoursRegular);
          const otHrs = pf(detailEntry.hoursOvertime);
          const rejNote = detailEntry.reviewerNotes || (isRejected && detailEntry.notes?.startsWith('[REJECTED:') ? detailEntry.notes.match(/\[REJECTED:\s*(.*?)\]/)?.[1] : '');
          const approvedReg = pf(detailEntry.approvedRegHours);
          const approvedOT = pf(detailEntry.approvedOTHours);
          const hasApprovedDiff = (approvedReg > 0 && approvedReg !== regHrs) || (approvedOT > 0 && approvedOT !== otHrs);
          return (
            <Stack gap="sm">
              <SimpleGrid cols={2}>
                <div><Text size="xs" c="dimmed">Date</Text><Text size="sm" fw={500}>{detailEntry.workDate}</Text></div>
                <div><Text size="xs" c="dimmed">Status</Text><Badge size="sm" variant="light" color={STATUS_COLORS[detailEntry.timesheetOnFile] || 'gray'}>{detailEntry.timesheetOnFile || 'draft'}</Badge></div>
                <div><Text size="xs" c="dimmed">Activity</Text><Text size="sm">{detailEntry.activityDescription || '-'}</Text></div>
                <div><Text size="xs" c="dimmed">Site / Location</Text><Text size="sm">{detailEntry.notes || '-'}</Text></div>
              </SimpleGrid>
              <Divider />
              <SimpleGrid cols={3}>
                <div><Text size="xs" c="dimmed">Regular Hours</Text><Text size="sm" fw={500}>{regHrs.toFixed(1)}</Text></div>
                <div><Text size="xs" c="dimmed">Overtime Hours</Text><Text size="sm" fw={500}>{otHrs.toFixed(1)}</Text></div>
                <div><Text size="xs" c="dimmed">Total Hours</Text><Text size="sm" fw={600}>{(regHrs + otHrs).toFixed(1)}</Text></div>
              </SimpleGrid>
              <SimpleGrid cols={3}>
                <div><Text size="xs" c="dimmed">Rate</Text><Text size="sm">{formatCurrency(detailEntry.rateRegular)}/hr</Text></div>
                <div><Text size="xs" c="dimmed">Fringe %</Text><Text size="sm">{detailEntry.fringeBenefitsPct || '-'}%</Text></div>
                <div><Text size="xs" c="dimmed">Shift Premium</Text><Text size="sm">{detailEntry.shiftPremium && detailEntry.shiftPremium !== 'none' ? (PREMIUM_OPTIONS.find(p => p.value === detailEntry.shiftPremium)?.label || detailEntry.shiftPremium) : 'None'}</Text></div>
              </SimpleGrid>
              <Divider />
              <SimpleGrid cols={3}>
                <div><Text size="xs" c="dimmed">Labor Cost</Text><Text size="sm" fw={500}>{formatCurrency(detailEntry.laborCost)}</Text></div>
                <div><Text size="xs" c="dimmed">Fringe Cost</Text><Text size="sm" fw={500}>{formatCurrency(detailEntry.fringeCost)}</Text></div>
                <div><Text size="xs" c="dimmed">Total Cost</Text><Text size="sm" fw={700} c={TEAL}>{formatCurrency(detailEntry.totalCost)}</Text></div>
              </SimpleGrid>
              {(detailEntry.accountCode || detailEntry.programCode) && (
                <SimpleGrid cols={2}>
                  <div><Text size="xs" c="dimmed">Account Code</Text><Text size="sm">{detailEntry.accountCode || '-'}</Text></div>
                  <div><Text size="xs" c="dimmed">Program Code</Text><Text size="sm">{detailEntry.programCode || '-'}</Text></div>
                </SimpleGrid>
              )}
              {hasApprovedDiff && (
                <Paper p="xs" withBorder bg="yellow.0">
                  <Text size="xs" fw={500} c="orange.8">Approved hours differ from logged:</Text>
                  <Text size="xs">Approved: {approvedReg.toFixed(1)} reg + {approvedOT.toFixed(1)} OT | Approved Cost: {formatCurrency(detailEntry.approvedCost)}</Text>
                </Paper>
              )}
              {isRejected && rejNote && (
                <Paper p="xs" withBorder bg="red.0">
                  <Text size="xs" fw={500} c="red.8">Reviewer Notes:</Text>
                  <Text size="sm">{rejNote}</Text>
                </Paper>
              )}
              <Divider />
              <Group justify="flex-end">
                {(isDraft || isRejected) && (
                  <>
                    <Button variant="light" color="blue" size="xs" leftSection={<IconEdit size={14} />}
                      onClick={() => { setDetailEntry(null); openEdit(detailEntry); }}>Edit</Button>
                    <Button variant="light" color="red" size="xs" leftSection={<IconTrash size={14} />}
                      onClick={() => { setDetailEntry(null); setDeleteTarget(detailEntry); }}>Delete</Button>
                  </>
                )}
                <Button variant="light" color="gray" size="xs" onClick={() => setDetailEntry(null)}>Close</Button>
              </Group>
            </Stack>
          );
        })()}
      </Modal>

      {/* Edit Modal */}
      <Modal opened={!!editingEntry} onClose={() => setEditingEntry(null)} title="Edit Time Entry" size="md">
        {editingEntry && (
          <Stack gap="sm">
            <Text size="sm" c="dimmed">Date: {editingEntry.workDate}</Text>
            <SimpleGrid cols={2}>
              <TextInput label="Regular Hours" value={editForm.regHours}
                onChange={e => setEditForm(f => ({ ...f, regHours: e.target.value }))} size="sm" />
              <TextInput label="Overtime Hours" value={editForm.otHours}
                onChange={e => setEditForm(f => ({ ...f, otHours: e.target.value }))} size="sm" />
            </SimpleGrid>
            <Select label="Activity" data={TASK_OPTIONS} value={editForm.task || null}
              onChange={v => setEditForm(f => ({ ...f, task: v || '' }))} clearable size="sm" />
            <TextInput label="Site / Location" value={editForm.site}
              onChange={e => setEditForm(f => ({ ...f, site: e.target.value }))} size="sm" />
            <TextInput label="Notes" value={editForm.notes}
              onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} size="sm" />
            <Group justify="flex-end">
              <Button variant="light" color="gray" onClick={() => setEditingEntry(null)}>Cancel</Button>
              <Button color={TEAL} onClick={handleEditSave} loading={editSaving}>Save Changes</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Delete Confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Time Entry" size="sm">
        <Text size="sm" mb="md">
          Delete entry for <strong>{deleteTarget?.workDate}</strong> — {pf(deleteTarget?.hoursRegular).toFixed(1)} reg + {pf(deleteTarget?.hoursOvertime).toFixed(1)} OT hrs?
        </Text>
        <Group justify="flex-end">
          <Button variant="light" color="gray" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="red" onClick={handleDelete}>Delete</Button>
        </Group>
      </Modal>
    </Stack>
  );
}
